import { useMemo } from "react"

import { useCompany } from "~/hooks/useCompany"

/**
 * Column IDs the company has turned off in the "available columns" settings. These are hidden from
 * every table's grid (and column chooser), not just the picker. Returned as a Set for cheap lookups.
 */
export const useDisabledColumnIdSet = (): Set<string> => {
  const { data: company } = useCompany()
  return useMemo(() => new Set(company?.disabledColumnIds ?? []), [company?.disabledColumnIds])
}

/**
 * Filters company-disabled columns out of a displayed-column list, for use when building the
 * columns a grid actually renders. Stored view state should keep the original ids so re-enabling a
 * column in settings restores it — only the rendered set is narrowed here.
 */
export const useVisibleColumnIds = (displayedColumnIds: string[]): string[] => {
  const disabledColumnIds = useDisabledColumnIdSet()
  return useMemo(
    () => displayedColumnIds.filter((id) => !disabledColumnIds.has(id)),
    [displayedColumnIds, disabledColumnIds],
  )
}
